import { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';

import api from '../../../services/api';
import { backgroundRequest } from '../../../services/sessionActivity';

// Faz B (spec 2026-08-07 §4, Task 5) — kalibrasyon oturumu FE veri katmanı.
// Desen: evaluation-periods/config (liste+action hook) + performance-settings/
// config.useRatingScalesApi (SAYFALANMAYAN liste — bkz. yorum orada, aynısı
// v1/calibration-sessions için de geçerli: ICalibrationSessionService.List
// düz bir dizi döner, PaginatedList DEĞİL — bkz. task-4-report.md/Controller).

export const SESSION_STATUS_VALUES = ['draft', 'active', 'closed', 'cancelled'];

export const STATUS_COLORS = {
  draft: { color: '#64748B', bg: '#F1F5F9' },
  active: { color: '#0291EF', bg: '#EFF6FF' },
  closed: { color: '#16A34A', bg: '#DCFCE7' },
  cancelled: { color: '#E53935', bg: '#FEF2F2' },
};

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

// PeriodWizard.js'in kapsam seçici deseniyle BİREBİR (spec §4 "kapsam: dönem
// sihirbazının kapsam bileşen deseni") — bağımsız tekrar (repo konvansiyonu,
// bkz. CalibrationSessionDataAccess.ResolveUserNamesAsync yorumu).
export const SCOPE_TYPES = ['company', 'department', 'group'];

export const searchDepartments = async (term) => {
  const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
  const res = await api.get('/v1/departments', { params });
  return (res.data?.data?.data || []).map((d) => ({ id: d.id, name: d.name }));
};

export const searchUserGroups = async (term) => {
  const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
  const res = await api.get('/v1/user-groups', { params });
  return (res.data?.data?.data || []).map((g) => ({ id: g.id, name: g.name }));
};

// ═══════ PMF-14 (Faz 2, 2026-09-11) — SEÇİCİ YANLIŞ KİMLİK SAKLIYORDU ═══════
//
// CalibrationSessionMemberRequest.UserId gerçek login User.Id'sidir, Employee.Id
// DEĞİL: backend onu JWT'deki kullanıcı kimliğiyle karşılaştırır
// (CalibrationSessionDataAccess.IsMemberAsync, liste süzgeci ve moderatör
// kontrolünün ÜÇÜ de `m.UserId == userId`), ad çözümü de Users→Employees
// üzerinden yapar (ResolveUserNamesAsync).
//
// Kod `e.userId || e.id` yazıyordu ve doğru niyeti taşıyordu — AMA hiçbir çalışan
// DTO'su `userId` TAŞIMIYORDU (ne EmployeeResponse ne kısıtlı
// EmployeeListItemResponse). Yani düşüş dalı her zaman çalışıyor ve DAİMA
// Employee.Id saklanıyordu. Normal işe alım akışı User.Id = Employee.Id yazdığı
// için çoğu kiracıda kaza eseri çalışıyor; eşitliğin bozulduğu her kullanıcıda
// (yer tutucu/ikinci Users satırı — reference_onay_kimlik_ailesi_yer_tutucu_user)
// moderatör ya da üye ızgarayı HİÇ açamıyor, kalıcı 403 alıyordu.
//
// Düzeltmenin arka uç yarısı: her iki DTO'ya da `UserId` eklendi ve liste ucu
// onu kimlik ailesini çözerek (aktif Users satırı tercih edilir) dolduruyor.
// Düşüş dalı KORUNUYOR — kullanıcı bağı olmayan (yalnız personel kaydı) çalışanda
// alan null gelir ve o kişi zaten oturuma giriş yapamaz.
//
// Arama alanı `firstName[like]` DEĞİL `fullName[like]`: kullanıcı "Ahmet Yılmaz"
// yazdığında eski hâl hiçbir sonuç bulamıyordu (soyad firstName'de aranıyordu).
export const searchEmployeesAsUsers = async (term) => {
  const params = term ? { 'fullName[like]': term, pageSize: 20 } : { pageSize: 20 };
  const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
  const r = res.data?.data;
  const items = Array.isArray(r) ? r : (r?.data || []);
  return items.map((e) => ({
    id: e.userId || e.id,
    name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
    profilePicture: e.profilePicture,
  }));
};

const fetchPeriodsByStatus = async (status) => {
  const res = await api.get('/v1/evaluation-periods', { params: { 'status[eq]': status, pageSize: 50 } });
  const r = res.data?.data;
  return Array.isArray(r) ? r : (r?.data || []);
};

// Create/Activate spec §6 kabul ettiği üç dönem durumu — active|evaluation|
// calibration (draft/pending_close/closed/cancelled'da taslak KURULAMAZ, bkz.
// CalibrationSessionDataAccess.Create InvalidState dalı). Final-review fix
// (C1) — bayrak da BE'de zorlanır (Create/Activate invalidState), bu yüzden
// seçicide yalnız calibrationSessionEnabled===true dönemler listelenir;
// aksi halde kullanıcı bayraksız dönemi seçip BE'den 409 alırdı.
export const useEligiblePeriods = () => {
  const [periods, setPeriods] = useState([]);
  const [periodsFetching, setPeriodsFetching] = useState(true);

  useEffect(() => {
    setPeriodsFetching(true);
    Promise.all([
      fetchPeriodsByStatus('active'),
      fetchPeriodsByStatus('evaluation'),
      fetchPeriodsByStatus('calibration'),
    ])
      .then(([active, evaluating, calibrating]) => setPeriods(
        [...active, ...evaluating, ...calibrating].filter((p) => p.calibrationSessionEnabled === true),
      ))
      .catch(() => setPeriods([]))
      .finally(() => setPeriodsFetching(false));
  }, []);

  return { periods, periodsFetching };
};

// Liste sayfasının Dönem kolonu için isim çözümü — TÜM dönemler (durumdan
// bağımsız, geçmiş oturumlar kapanmış/iptal dönemlere de bağlı olabilir).
export const useAllPeriodsMap = () => {
  const [periodsMap, setPeriodsMap] = useState({});

  useEffect(() => {
    api.get('/v1/evaluation-periods', { params: { pageSize: 200 } })
      .then((res) => {
        const r = res.data?.data;
        const items = Array.isArray(r) ? r : (r?.data || []);
        const map = {};
        items.forEach((p) => { map[p.id] = p.name; });
        setPeriodsMap(map);
      })
      .catch(() => setPeriodsMap({}));
  }, []);

  return periodsMap;
};

// Period.RatingScaleId doluysa seviyeleri getirir (A-only dönemde skala
// yoktur — Hedefler adımı o zaman tamamen atlanır, bkz. NewSessionModal).
export const useScaleLevels = (ratingScaleId) => {
  const [levels, setLevels] = useState([]);
  const [levelsFetching, setLevelsFetching] = useState(false);

  useEffect(() => {
    if (!ratingScaleId) {
      setLevels([]);
      return;
    }
    setLevelsFetching(true);
    api.get(`/v1/rating-scales/${ratingScaleId}`)
      .then((res) => setLevels(res.data?.data?.levels || []))
      .catch(() => setLevels([]))
      .finally(() => setLevelsFetching(false));
  }, [ratingScaleId]);

  return { levels, levelsFetching };
};

// --- Yeni Oturum formu: pür yardımcılar (DOM'suz test edilebilir) ---------

// Boş/null girişleri sayıma KATMAZ (kullanıcı henüz dokunmadığı seviyeyi 0
// olarak saymaz) — MyGoalsPage.config.computeWeightBand ile aynı ilke.
export function computeTargetsSum(levelTargets) {
  const values = Object.values(levelTargets || {})
    .filter((v) => v !== '' && v !== null && v !== undefined)
    .map(Number)
    .filter((v) => Number.isFinite(v));
  const sum = values.reduce((acc, v) => acc + v, 0);
  const rounded = Math.round(sum * 100) / 100;
  return { sum: rounded, isOverLimit: rounded > 100 };
}

// req.LevelTargets sözleşmesi: Dictionary<Guid, decimal>? — boşsa null
// (hedefsiz oturum, spec §3 "hedefsiz oturum gerekçesiz kapanır" dalı).
export function buildLevelTargetsPayload(levelTargets) {
  const entries = Object.entries(levelTargets || {})
    .filter(([, v]) => v !== '' && v !== null && v !== undefined && Number.isFinite(Number(v)));
  if (entries.length === 0) return null;
  return Object.fromEntries(entries.map(([levelId, v]) => [levelId, Number(v)]));
}

// CalibrationSessionRequest ile BİREBİR (Dtos/Performance/CalibrationSessionDtos.cs)
// — camelCase alan adları JSON sözleşmesi (periodId/name/scopeType/scopeTargetIds/
// levelTargets/tolerancePct/members[{userId,role}]).
export function buildSessionCreatePayload(form) {
  const members = [];
  if (form.moderatorUserId) {
    members.push({ userId: form.moderatorUserId, role: 'moderator' });
  }
  (form.memberUserIds || []).forEach((userId) => {
    if (userId && userId !== form.moderatorUserId) {
      members.push({ userId, role: 'member' });
    }
  });

  return {
    periodId: form.periodId,
    name: (form.name || '').trim(),
    scopeType: form.scopeType,
    scopeTargetIds: form.scopeType === 'company' ? [] : (form.scopeTargetIds || []),
    levelTargets: buildLevelTargetsPayload(form.levelTargets),
    tolerancePct: form.tolerancePct === '' || form.tolerancePct === null || form.tolerancePct === undefined
      ? null
      : Number(form.tolerancePct),
    members,
  };
}

// Kaydet butonu devre dışı kontrolü — BE ValidateAsync sırasıyla BİREBİR
// (nameRequired → invalidScope → moderatorRequired → invalidTargets), FE
// erken uyarı amaçlı (BE zaten reddeder, bkz. RatingScalesTab.js'in "FE erken
// uyarı" yorumu — aynı ilke).
export function isSessionFormValid(form) {
  if (!form.periodId) return false;
  if (!form.name || !form.name.trim()) return false;
  if (!SCOPE_TYPES.includes(form.scopeType)) return false;
  if (form.scopeType !== 'company' && (!form.scopeTargetIds || form.scopeTargetIds.length === 0)) return false;
  if (!form.moderatorUserId) return false;
  if (form.tolerancePct !== '' && form.tolerancePct !== null && form.tolerancePct !== undefined
    && Number(form.tolerancePct) < 0) return false;
  if (computeTargetsSum(form.levelTargets).isOverLimit) return false;
  return true;
}

export const NEW_SESSION_INITIAL_FORM = {
  periodId: null,
  name: '',
  scopeType: 'company',
  scopeTargetIds: [],
  moderatorUserId: null,
  memberUserIds: [],
  levelTargets: {},
  tolerancePct: 5,
};

// --- Liste sayfası: CRUD + yaşam döngüsü aksiyonları ----------------------

export const useCalibrationSessionsApi = () => {
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/calibration-sessions')
      .then((res) => setData(Array.isArray(res.data?.data) ? res.data.data : []))
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const createSession = useCallback(async (payload) => {
    try {
      const res = await api.post('/v1/calibration-sessions', payload);
      refresh();
      return res.data?.data?.id || true;
    } catch {
      return null;
    }
  }, [refresh]);

  // verb: activate|cancel
  const runLifecycleAction = useCallback(async (id, verb) => {
    try {
      await api.post(`/v1/calibration-sessions/${id}/${verb}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const deleteDraftSession = useCallback(async (id) => {
    try {
      await api.delete(`/v1/calibration-sessions/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  return {
    data, fetching, refresh, createSession, runLifecycleAction, deleteDraftSession,
  };
};

// --- Detay: moderatör tespiti için oturum kaydı (üye listesi) ------------

export const useSessionDetail = (sessionId) => {
  const [session, setSession] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    if (!sessionId) return;
    setFetching(true);
    api.get(`/v1/calibration-sessions/${sessionId}`)
      .then((res) => setSession(res.data?.data || null))
      .finally(() => setFetching(false));
  }, [sessionId, refreshTrigger]);

  return { session, fetching, refresh };
};

// --- Izgara: polling + fark-tespiti + taşıma/kapanış ----------------------

const GRID_POLL_INTERVAL_MS = 15000;

// SessionGridDto.LastChangedAt karşılaştırması (spec B5) — önceki değer yoksa
// (ilk yükleme) her zaman uygula; doluysa YALNIZ değiştiyse uygula. Polling
// motorundan AYRIŞTIRILMIŞ pür fonksiyon — timer/act olmadan doğrudan test
// edilebilir.
export function gridChanged(prevLastChangedAt, nextGrid) {
  if (!nextGrid) return false;
  if (!prevLastChangedAt) return true;
  return nextGrid.lastChangedAt !== prevLastChangedAt;
}

export const useSessionGrid = (sessionId) => {
  const [grid, setGrid] = useState(null);
  const [fetching, setFetching] = useState(true);
  const lastChangedAtRef = useRef(null);

  // `background`: 15 sn yoklaması — açık bırakılmış oturum ekranı hareketsizlik zaman aşımını
  // ertelemesin (sunucu pencereyi uzatmaz). İlk yükleme ve kullanıcı yenilemesi normal istektir.
  const fetchGrid = useCallback(async (options) => {
    if (!sessionId) return;
    try {
      const background = options?.background === true;
      const res = await api.get(`/v1/calibration-sessions/${sessionId}/grid`, background ? backgroundRequest() : undefined);
      const nextGrid = res.data?.data || null;
      if (gridChanged(lastChangedAtRef.current, nextGrid)) {
        lastChangedAtRef.current = nextGrid.lastChangedAt;
        setGrid(nextGrid);
      }
    } finally {
      setFetching(false);
    }
  }, [sessionId]);

  // İlk yükleme.
  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  // 15sn polling — unmount'ta temizlenir (spec B5).
  useEffect(() => {
    const timer = setInterval(() => fetchGrid({ background: true }), GRID_POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchGrid]);

  const moveEntry = useCallback(async (req) => {
    try {
      await api.post(`/v1/calibration-sessions/${sessionId}/moves`, req);
      return { ok: true };
    } catch (err) {
      return { ok: false, message: err.response?.data?.message };
    } finally {
      // Kendi aksiyonumuzun ardından 15sn beklemeden ekranı tazele (başarısız
      // dahi — session durumu araya girmiş olabilir, ör. başka moderatör
      // kapatmış).
      fetchGrid();
    }
  }, [sessionId, fetchGrid]);

  const closeSession = useCallback(async (justification) => {
    try {
      const res = await api.post(
        `/v1/calibration-sessions/${sessionId}/close`,
        { justification: justification || null },
        {
          // justificationRequired/missingProposals kapanış diyaloğunun KENDİ
          // UI'ında (gerekçe alanı / eksik liste) ele alınır — interceptor'ın
          // genel toast'ı burada YİNELEME olur (api.js'in "bir çağıran tam
          // olarak bir bilinen sonucu özel olarak işliyor" deseni).
          skipErrorToast: [
            'performance.calibrationSession.justificationRequired',
            'performance.calibrationSession.missingProposals',
          ],
        },
      );
      return { ok: true, data: res.data?.data };
    } catch (err) {
      return { ok: false, message: err.response?.data?.message, data: err.response?.data?.data };
    } finally {
      fetchGrid();
    }
  }, [sessionId, fetchGrid]);

  return {
    grid, fetching, refresh: fetchGrid, moveEntry, closeSession,
  };
};
